import { getCookie, setCookie, removeCookie } from '@visa/lib-cookie';

const ONE_YEAR_IN_MS = 52 * 7 * 24 * 60 * 60 * 1000;
const SIX_MONTHS_IN_MS = ONE_YEAR_IN_MS / 2;

export type ErmCookieValue = { authRef: string; date: number; username: string };
export type SsiCookieValue = { date: number; username: string };

export function decodeCookieValue(cookieName: string) {
  try {
    const decodedCookieValue = decodeURIComponent(getCookie(cookieName) as string);
    const cookieValue = JSON.parse(decodedCookieValue) as ErmCookieValue | SsiCookieValue | null;

    if (typeof cookieValue?.date === 'string') {
      cookieValue.date = parseInt(cookieValue.date, 10);
    }

    return cookieValue;
  } catch (_) {
    return null;
  }
}

type RememberMeCookieValue = {
  it: number;
  ut: number;
  ex: number;
  ji: string;
  la: { lm: string; ld: null };
  fo: { fm: string; fd: null }[];
};

export function getRememberMeCookieValue(): RememberMeCookieValue | null {
  const remembermeCookie = getCookie('remember_me');

  if (!remembermeCookie) {
    return null;
  }

  try {
    // Sample base64 encoded value:
    // eyJpdCI6MTU1ODA5NDQwMCwidXQiOjE1NjEzODg0MjUsImV4IjoxNTc3MTk5NjAwLCJqaSI6IlB3WHZzVkZYb3pUSm5xcC16UWRjIiwibGEiOnsibG0iOiJqbyoqKkBnbWFpbC5jb20iLCJsZCI6bnVsbH0sImZvIjpbeyJmbSI6ImthKioqQGdtYWlsLmNvbSIsImZkIjpudWxsfSx7ImZtIjoiY2EqKipAb3V0bG9vay5jb20iLCJmZCI6bnVsbH1dfQ==
    return JSON.parse(window.atob(remembermeCookie));
  } catch (_) {
    return null;
  }
}

export function getUsernameFromRememberMe() {
  return getRememberMeCookieValue()?.la.lm ?? null;
}

export function setErmEnabled(authRef?: string) {
  // F11669 get the username from the remember_me cookie
  const username = getUsernameFromRememberMe();
  setCookie(
    'ermEnabled',
    JSON.stringify({
      authRef,
      // By default set cookie expiry to today's date + 6 months timestamp
      date: String(Date.now() + SIX_MONTHS_IN_MS),
      username
    })
  );
}

export function setSsiEnabled() {
  // F11669 get the username from the remember_me cookie
  const username = getUsernameFromRememberMe();
  setCookie(
    'ssiEnabled',
    JSON.stringify({
      date: String(Date.now() + SIX_MONTHS_IN_MS),
      username
    })
  );
}

/**
 * Invoked by isRecognized/signout functionality as part of sign-in or username cookie change.
 */
export function deleteSsiErmCookies() {
  const ssiErmCookies = ['ermEnabled', 'ssiEnabled', 'ssiDeclined'];
  for (const cookie of ssiErmCookies) {
    removeCookie(cookie);
  }
}

function isValidTimestamp(savedTimestampString?: number) {
  if (!savedTimestampString) {
    return false;
  }

  return savedTimestampString >= Date.now();
}

export function getErmEnabledCookieValue() {
  return decodeCookieValue('ermEnabled') as ErmCookieValue | null;
}

export function getSsiEnabledCookieValue() {
  return decodeCookieValue('ssiEnabled') as SsiCookieValue | null;
}

export function getUsernameFromErmCookie() {
  const ermEnabled = getErmEnabledCookieValue();
  return ermEnabled?.username;
}

export function getUsernameFromSsiCookie() {
  const ssiEnabled = getSsiEnabledCookieValue();
  return ssiEnabled?.username;
}

export function hasValidSsiCookie() {
  const ssiEnabled = getSsiEnabledCookieValue();
  const rememberMeUsername = getUsernameFromRememberMe();
  const ssiUsername = ssiEnabled?.username ?? null;

  if (ssiUsername && rememberMeUsername && rememberMeUsername !== ssiUsername) {
    deleteSsiErmCookies();
    return false;
  }

  return isValidTimestamp(ssiEnabled?.date);
}

export function hasValidErmCookie() {
  const ermEnabled = getErmEnabledCookieValue();
  const rememberMeUsername = getUsernameFromRememberMe();
  const ermUsername = ermEnabled?.username ?? null;

  if (ermUsername && rememberMeUsername && rememberMeUsername !== ermUsername) {
    deleteSsiErmCookies();
    return false;
  }

  return Boolean(ermEnabled?.authRef && isValidTimestamp(ermEnabled.date));
}

export const exists = (name: string) => {
  return getCookie(name);
};
